/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { URLHelper } from 'sarsaparilla';

import { defaultSearchLocation, maximumMapSearchResults } from './map.constants';
import type {
    GeoSearchArgs,
    GeoSearchResponse,
    MapInventoryItem,
    MapSearchResultLocation,
    MapState,
} from './map.types';

const initialState: MapState = {
    mapInventory: [],
    inventoryIsLoaded: false,
    mapSearchResultLocation: defaultSearchLocation,
};

/**
 * Fetches map inventory for the given bounding box from the `/search/geo` endpoint.
 * Facility-group (`fg`) filters are appended as repeated query params when provided.
 */
export const fetchMapInventory = createAsyncThunk(
    'map/fetchMapInventory',
    async (geoSearchArgs: GeoSearchArgs): Promise<MapInventoryItem[]> => {
        const { fg, ...restArgs } = geoSearchArgs;
        const args = {
            ...restArgs,
            size: maximumMapSearchResults,
        };

        let url = URLHelper.urlWithParams(`${process.env.API}/search/geo`, args);

        if (Array.isArray(fg) && fg.length > 0) {
            const fgParams = fg
                .map((value) => `fg=${encodeURIComponent(value)}`)
                .join('&');
            url = `${url}&${fgParams}`;
        }

        const response = await axios.get<GeoSearchResponse>(url);
        return response.data.results;
    }
);

const mapSlice = createSlice({
    name: 'map',
    initialState,
    reducers: {
        updateMapSearchResultLocation(
            state,
            action: PayloadAction<MapSearchResultLocation>
        ) {
            state.mapSearchResultLocation = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchMapInventory.fulfilled, (state, action) => {
                state.mapInventory = action.payload;
                state.inventoryIsLoaded = true;
            })
            .addCase(fetchMapInventory.rejected, (state) => {
                state.mapInventory = [];
                state.inventoryIsLoaded = true;
            });
    },
});

export const { updateMapSearchResultLocation } = mapSlice.actions;

export default mapSlice.reducer;
